/**
 * C50 / C80 / D50 — ISO 3382 clarity and definition metrics, derived purely
 * from the decay curve already exposed on RT60Response (RT60DecayCurve).
 * No new backend computation: the Schroeder-integrated energy decay curve
 * `edcDb` already IS the reverse-cumulative energy sum
 * (edcLinear(t) = sum of all energy arriving at t or later), so the ratio of
 * early to late energy at a cutoff is recoverable from two samples of that
 * curve — see `ratioAtCutoff`'s derivation below.
 *
 * C50/C80 (dB) — ratio of energy arriving before the cutoff to energy
 * arriving after it. Higher = clearer/more intelligible. C50 is the speech-
 * clarity convention (50 ms cutoff); C80 is the music-clarity convention
 * (80 ms). D50 (%) — same split expressed as a fraction of total energy
 * rather than a ratio; the speech "definition" convention.
 */

import { NO_ENERGY_SENTINEL_DB, type RT60DecayCurve } from './registry';

export interface ClarityFits {
  bandHz: number[];
  c50Db: number[];
  c80Db: number[];
  d50Pct: number[];
}

/**
 * Fraction of TOTAL energy still remaining (not yet arrived) at `cutoffMs`.
 *
 * `edcDb(t) = 10*log10(edcLinear(t) / e0)` where `edcLinear(t)` is the
 * Schroeder backward-integrated energy from t to the end of the simulated
 * window (ray_rt60.py's `edc`) and `e0 = edcLinear(0)` is total energy.
 * `edcDb(0) == 0` always (by construction). So:
 *   r = edcLinear(cutoff) / e0 = 10^(edcDb(cutoff)/10)   — "late" fraction
 *   1 - r = (e0 - edcLinear(cutoff)) / e0                — "early" fraction
 *
 * Returns null when the curve is too short to sample the cutoff, is the
 * no-energy sentinel (see ray_rt60.py), or r sits at a degenerate extreme
 * (all energy on one side — the ratio would be +-Infinity).
 */
function lateEnergyFraction(edcDb: number[], dt: number, cutoffMs: number): number | null {
  if (!edcDb.length || !(dt > 0)) return null;
  if (edcDb[0]! < NO_ENERGY_SENTINEL_DB + 1) return null; // sentinel: no energy in this band
  const idx = Math.round(cutoffMs / 1000 / dt);
  if (idx <= 0 || idx >= edcDb.length) return null;
  const r = 10 ** (edcDb[idx]! / 10);
  if (!(r > 1e-6) || !(r < 1 - 1e-6)) return null;
  return r;
}

export function computeC50(edcDb: number[], dt: number): number | null {
  const r = lateEnergyFraction(edcDb, dt, 50);
  return r == null ? null : 10 * Math.log10((1 - r) / r);
}

export function computeC80(edcDb: number[], dt: number): number | null {
  const r = lateEnergyFraction(edcDb, dt, 80);
  return r == null ? null : 10 * Math.log10((1 - r) / r);
}

export function computeD50(edcDb: number[], dt: number): number | null {
  const r = lateEnergyFraction(edcDb, dt, 50);
  return r == null ? null : (1 - r) * 100;
}

/** All three metrics, for every band in a decay curve. NaN (not null) for a
 *  band a metric couldn't be computed for, matching `RT60Fits`' convention
 *  of a fixed-length numeric array aligned with `bandHz`. */
export function computeClarityFits(decay: RT60DecayCurve): ClarityFits {
  const dt = decay.timeS.length >= 2 ? decay.timeS[1]! - decay.timeS[0]! : 0.01;
  const c50Db: number[] = [];
  const c80Db: number[] = [];
  const d50Pct: number[] = [];
  decay.bandHz.forEach((_, i) => {
    const curve = decay.edcDb[i] ?? [];
    c50Db.push(computeC50(curve, dt) ?? NaN);
    c80Db.push(computeC80(curve, dt) ?? NaN);
    d50Pct.push(computeD50(curve, dt) ?? NaN);
  });
  return { bandHz: decay.bandHz, c50Db, c80Db, d50Pct };
}
